/**
 * «Шаблоны»: все быстрые ответы «Чатов» текстом на одном экране и то, что мы на самом деле пишем
 * клиентам. Спека `docs/specs/2026-10-08-chat-phrases-design.md`.
 *
 * «Шаблоны» — чтение: общие или личные выбранного сотрудника, все категории раскрыты (правят их
 * по-прежнему в «Чатах», кнопкой у «Отправить»). «Частые ответы» — самые частые наши сообщения
 * по всей переписке: одинаковые тексты слиты, имена заменены на {имя}; отбор — кто отправлял,
 * канал, период. Числа — те же, что у `prokat-runner chat-phrases`.
 */
import { useGetIdentity } from "@refinedev/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { NoAccess } from "@/components/NoAccess";
import { CLIENT_CHATS, has } from "@/lib/access";
import { loadLibrary, loadPhrases, type Phrase, type PhraseParams } from "@/lib/chatPhrases";
import { mskTime } from "@/lib/dates";
import { AiTemplatesView } from "@/pages/AiTemplatesView";
import type { CurrentUser } from "@/lib/providers";

const CHANNEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  max: "MAX",
  avito: "Авито",
  vk: "ВК",
  instagram: "Instagram",
};
const PERIODS = [
  { days: 0, label: "Всё время" },
  { days: 365, label: "Год" },
  { days: 90, label: "90 дней" },
  { days: 30, label: "30 дней" },
];
const NO_AUTHOR = "-";

const num = (n: number) => n.toLocaleString("ru-RU");
const dmy = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(2, 4)}`;
const plain = (s: string) => s.toLowerCase().replaceAll("ё", "е");
/** «1 раз», «2 раза», «5 раз». */
const times = (n: number) => `${num(n)} ${n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "раза" : "раз"}`;
const chip = (on: boolean) =>
  `rounded-[6px] px-3 py-1.5 text-[13px] font-medium ${on ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"}`;
const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";

/** Параметры экрана живут в адресе: ссылку на отбор можно переслать. */
function useParams() {
  const [params, setParams] = useSearchParams();
  const get = (key: string) => params.get(key) ?? "";
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };
  return [get, set] as const;
}

/** Поле поиска: в адрес текст уходит с паузой, чтобы не дёргать запрос на каждую букву. */
function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return (
    <label className="flex h-8 w-[260px] items-center gap-2 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 focus-within:border-accent">
      <Search size={13} className="flex-none text-fg-faint" />
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-[13px] text-fg outline-none placeholder:text-fg-faint"
      />
    </label>
  );
}

/** Текст ответа: подстановка `{имя}` выделена — это место, где у каждого сотрудника своё имя. */
function PhraseText({ text }: { text: string }) {
  const parts = text.split("{имя}");
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="rounded bg-accent-dim px-1 text-accent-text">имя</span>}
          {part}
        </Fragment>
      ))}
    </>
  );
}

function Sent({ n }: { n: number | null }) {
  if (n === null) return <span className="text-[11.5px] text-fg-faint">в переписке не встречался</span>;
  return (
    <span className="rounded bg-ok-bg px-1.5 py-0.5 text-[11.5px] tabular-nums text-ok" title="Сколько раз такой ответ отправляли клиентам (по всей переписке «Чатов»)">
      отправляли {times(n)}
    </span>
  );
}

function LibraryView({ get, set }: { get: (k: string) => string; set: (p: Record<string, string>) => void }) {
  const { data: me } = useGetIdentity<CurrentUser>();
  const { hash } = useLocation();
  // Из «Частых ответов» сюда приходят по ссылке на шаблон: `#t-<id>` — показать и подсветить его.
  const focus = hash.startsWith("#t-") ? Number(hash.slice(3)) : null;
  const personal = get("scope") === "personal";
  const common = useQuery({ queryKey: ["templates-library", null], queryFn: () => loadLibrary(null), refetchInterval: 60_000 });
  const owners = common.data?.owners ?? [];
  // Личные по умолчанию — свои, если они есть; иначе первого сотрудника из списка.
  const owner = Number(get("owner")) || (owners.find((o) => o.id === me?.id) ?? owners[0])?.id || null;
  const own = useQuery({
    queryKey: ["templates-library", owner],
    queryFn: () => loadLibrary(owner),
    enabled: personal && owner !== null,
    refetchInterval: 60_000,
  });
  const shown = personal ? (owner === null ? common.data && { ...common.data, categories: [] } : own.data) : common.data;
  const failed = personal ? own.error : common.error;
  const words = plain(get("q")).split(/\s+/).filter(Boolean);
  const categories = (shown?.categories ?? [])
    .map((c) => ({
      ...c,
      templates: c.templates.filter((t) => {
        const hay = plain(`${c.name} ${t.title} ${t.text}`);
        return words.every((w) => hay.includes(w));
      }),
    }))
    .filter((c) => c.templates.length > 0);
  const total = categories.reduce((n, c) => n + c.templates.length, 0);
  const ready = shown !== undefined;
  useEffect(() => {
    if (focus && ready) document.getElementById(`t-${focus}`)?.scrollIntoView({ block: "center" });
  }, [focus, ready]);
  const used = categories.reduce((n, c) => n + c.templates.filter((t) => t.sent !== null).length, 0);
  const commonTotal = (common.data?.categories ?? []).reduce((n, c) => n + c.templates.length, 0);
  const personalTotal = owners.reduce((n, o) => n + o.templates, 0);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="какие шаблоны">
        <div role="tablist" aria-label="Какие шаблоны" className="flex w-fit gap-1 rounded-[var(--radius-ui)] border border-border p-1" data-annot="общие или личные">
          <button type="button" role="tab" aria-selected={!personal} onClick={() => set({ scope: "", owner: "" })} className={chip(!personal)}>
            Общие · {commonTotal}
          </button>
          <button type="button" role="tab" aria-selected={personal} onClick={() => set({ scope: "personal" })} className={chip(personal)}>
            Личные · {personalTotal}
          </button>
        </div>
        {personal && owners.length > 0 && (
          <Combo
            label="Чьи"
            value={String(owner ?? "")}
            options={owners.map((o) => ({ value: String(o.id), label: o.id === me?.id ? `${o.name} (я)` : o.name, meta: String(o.templates) }))}
            onChange={(v) => set({ owner: v })}
            placeholder="имя сотрудника"
            width={260}
          />
        )}
        <SearchBox value={get("q")} onChange={(v) => set({ q: v })} placeholder="слова в названии или тексте" />
        {shown && (
          <span className="text-[12.5px] text-fg-muted">
            {num(total)} шаблонов в {categories.length} категориях · отправляли {used}
          </span>
        )}
      </div>

      {failed && <div className={`${card} p-4 text-[13px] text-err`}>{failed.message}</div>}
      {personal && owners.length === 0 && common.data && (
        <div className={`${card} p-4 text-[13px] text-fg-muted`}>Личных шаблонов пока ни у кого нет — их заводят в «Чатах», кнопкой шаблонов у «Отправить».</div>
      )}
      {shown && categories.length === 0 && !(personal && owners.length === 0) && (
        <div className={`${card} p-4 text-[13px] text-fg-muted`}>{words.length > 0 ? "Под запрос ничего не нашлось." : "Шаблонов здесь нет."}</div>
      )}

      {categories.length > 0 && (
        <div className="grid min-h-0 grid-cols-[220px_minmax(0,1fr)] items-start gap-4">
          <nav className={`${card} sticky top-0 max-h-[calc(100vh-190px)] overflow-y-auto p-2 text-[12.5px]`} data-annot="категории шаблонов">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => document.getElementById(`cat-${c.id}`)?.scrollIntoView({ block: "start", behavior: "smooth" })}
                className="flex w-full items-baseline gap-2 rounded-[var(--radius-ui)] px-2 py-1 text-left text-fg-muted hover:bg-bg-hover hover:text-fg"
              >
                <span className="min-w-0 flex-1 truncate" title={c.name}>
                  {c.name}
                </span>
                <span className="tabular-nums text-fg-faint">{c.templates.length}</span>
              </button>
            ))}
          </nav>
          <div className="flex min-w-0 flex-col gap-5">
            {categories.map((c) => (
              <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-2" data-annot={`категория «${c.name}»`}>
                <h2 className="mb-2 text-[13.5px] font-semibold">
                  {c.name} <span className="font-normal text-fg-faint">· {c.templates.length}</span>
                </h2>
                <div className="grid items-start gap-2 xl:grid-cols-2">
                  {c.templates.map((t) => (
                    <article key={t.id} id={`t-${t.id}`} className={`${card} scroll-mt-2 p-3 ${t.id === focus ? "!border-accent" : ""}`} data-annot={`шаблон «${t.title}»`}>
                      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h3 className="text-[13px] font-semibold">{t.title}</h3>
                        <Sent n={t.sent} />
                      </div>
                      <p className="whitespace-pre-wrap break-words text-[13px] leading-snug text-fg-muted">{t.text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Authors({ p, pick }: { p: Phrase; pick: (name: string) => void }) {
  const top = p.authors.slice(0, 3);
  const rest = p.authors.slice(3);
  return (
    <div className="flex flex-col items-start gap-0.5">
      {top.map((a) => (
        <button
          key={a.name}
          type="button"
          onClick={() => pick(a.name || NO_AUTHOR)}
          title="Показать, чем пользуется этот сотрудник"
          className="whitespace-nowrap text-left hover:text-accent-text hover:underline"
        >
          <span className={a.name ? "" : "text-fg-faint"}>{a.name || "автор неизвестен"}</span>
          <span className="ml-1.5 tabular-nums text-fg-faint">{num(a.count)}</span>
        </button>
      ))}
      {rest.length > 0 && (
        <span className="text-fg-faint" title={rest.map((a) => `${a.name || "автор неизвестен"} — ${a.count}`).join("\n")}>
          и ещё {rest.length}
        </span>
      )}
    </div>
  );
}

function StatsView({ get, set }: { get: (k: string) => string; set: (p: Record<string, string>) => void }) {
  const p: PhraseParams = {
    author: get("author"),
    channel: get("channel") in CHANNEL ? get("channel") : "",
    days: PERIODS.some((x) => x.days === Number(get("days"))) ? Number(get("days")) : 0,
    short: get("short") === "1",
    auto: get("auto") === "1",
    q: get("q"),
  };
  const r = useQuery({ queryKey: ["templates-phrases", p], queryFn: () => loadPhrases(p), placeholderData: keepPreviousData });
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setOpen((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const data = r.data;
  const max = Math.max(1, ...(data?.phrases ?? []).map((x) => x.count));
  const authors = [
    { value: "", label: "Все сотрудники" },
    ...(data?.authors ?? []).map((a) => ({ value: a.name || NO_AUTHOR, label: a.name || "Автор неизвестен", meta: num(a.count) })),
  ];
  // Выбранного сотрудника нет в списке (другой период) — всё равно показываем его в поле.
  if (p.author && !authors.some((a) => a.value === p.author)) authors.push({ value: p.author, label: p.author, meta: "0" });
  const channels = [
    { value: "", label: "Все каналы" },
    ...(data?.channels ?? []).map((c) => ({ value: c.channel, label: CHANNEL[c.channel] ?? c.channel, meta: num(c.count) })),
  ];
  const byAuthor = p.author !== "";

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="отбор частых ответов">
        <Combo label="Кто отправлял" value={p.author} options={authors} onChange={(v) => set({ author: v })} placeholder="имя сотрудника" width={260} />
        <Combo label="Канал" value={p.channel} options={channels} onChange={(v) => set({ channel: v })} width={170} />
        <div role="group" aria-label="Период" className="flex items-center gap-1" data-annot="период">
          {PERIODS.map((x) => (
            <button
              key={x.days}
              type="button"
              aria-pressed={p.days === x.days}
              onClick={() => set({ days: x.days ? String(x.days) : "" })}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 ${
                p.days === x.days ? "border-accent bg-accent-dim text-accent-text" : "border-border hover:bg-bg-hover hover:text-fg"
              }`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <SearchBox value={p.q} onChange={(v) => set({ q: v })} placeholder="слова в тексте ответа" />
        <label className="flex cursor-pointer items-center gap-1.5" title="«да», «записали вас», «ожидаем» — ответы короче трёх слов">
          <input type="checkbox" checked={p.short} onChange={(e) => set({ short: e.target.checked ? "1" : "" })} />
          короткие
        </label>
        {!byAuthor && (
          <label className="flex cursor-pointer items-center gap-1.5" title="Ответы, которые ни разу не отправил названный сотрудник: автоответы открытых линий, рассылки, сообщества без подписи оператора">
            <input type="checkbox" checked={p.auto} onChange={(e) => set({ auto: e.target.checked ? "1" : "" })} />
            боты и рассылки
          </label>
        )}
      </div>

      {r.error && <div className={`${card} p-4 text-[13px] text-err`}>{r.error.message}</div>}
      {data && (
        <div className="text-[12.5px] text-fg-muted" data-annot="итог частых ответов">
          {data.built ? (
            <>
              Разных ответов <b className="font-semibold text-fg">{num(data.total.phrases)}</b>, отправок{" "}
              <b className="font-semibold text-fg">{num(data.total.sent)}</b>
              {data.total.phrases > data.phrases.length && <> · показаны {data.phrases.length} самых частых</>} · одинаковые тексты слиты, имена
              заменены на <span className="rounded bg-accent-dim px-1 text-accent-text">имя</span> · пересчитано {mskTime(data.built)}
            </>
          ) : (
            "Статистика ещё не собрана — фон пересчитывает её раз в 6 часов."
          )}
        </div>
      )}

      {data && data.phrases.length > 0 && (
        <section className={`${card} ${r.isPlaceholderData ? "opacity-60" : ""}`} data-annot="частые ответы">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead className="text-[11.5px] text-fg-muted">
                <tr>
                  <th className="w-10 px-3 py-2 text-right font-medium">№</th>
                  <th className="px-3 py-2 font-medium">Ответ</th>
                  <th className="px-3 py-2 text-right font-medium">Отправок</th>
                  <th className="px-3 py-2 font-medium">Кто отправляет</th>
                  <th className="px-3 py-2 font-medium">Каналы</th>
                  <th className="px-3 py-2 font-medium">Последний раз</th>
                  <th className="px-3 py-2 font-medium">В шаблонах</th>
                </tr>
              </thead>
              <tbody>
                {data.phrases.map((x, i) => (
                  <tr key={x.id} className="border-t border-border align-top" data-annot={`ответ № ${i + 1}`}>
                    <td className="px-3 py-2 text-right tabular-nums text-fg-faint">{i + 1}</td>
                    <td className="max-w-[560px] px-3 py-2">
                      <button type="button" onClick={() => toggle(x.id)} className="block w-full text-left" title={open.has(x.id) ? "Свернуть" : "Показать целиком"}>
                        <span className={`whitespace-pre-wrap break-words leading-snug ${open.has(x.id) ? "block" : "line-clamp-3"}`}>
                          <PhraseText text={x.text} />
                        </span>
                      </button>
                      {x.variants > 1 && <span className="mt-1 block text-[11.5px] text-fg-faint">слито написаний: {x.variants}</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <span className="text-[13.5px] font-semibold tabular-nums">{num(x.count)}</span>
                      {x.count !== x.total && (
                        <span className="block text-[11.5px] tabular-nums text-fg-faint" title="Всего отправок этого ответа: все сотрудники, каналы и годы">
                          из {num(x.total)}
                        </span>
                      )}
                      <span className="ml-auto mt-1 block h-1 w-20 rounded bg-neutral-bg">
                        <span className="ml-auto block h-1 rounded bg-accent" style={{ width: `${(x.count / max) * 100}%` }} />
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Authors p={x} pick={(name) => set({ author: name })} />
                    </td>
                    <td className="px-3 py-2 text-fg-muted">
                      {x.channels.map((c) => (
                        <span key={c.channel} className="block whitespace-nowrap">
                          {CHANNEL[c.channel] ?? c.channel} <span className="tabular-nums text-fg-faint">{num(c.count)}</span>
                        </span>
                      ))}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-fg-muted">
                      {dmy(x.last)}
                      <Link to={`/chats/${x.chat_id}`} className="block text-[11.5px] text-fg-faint hover:text-accent-text hover:underline" title="Чат, где этот ответ отправили последним">
                        открыть чат
                      </Link>
                      <span className="block text-[11.5px] text-fg-faint">с {dmy(x.first)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {x.templates.length === 0 ? (
                        <span className="text-fg-faint">нет</span>
                      ) : (
                        x.templates.map((t) => (
                          <Link
                            key={t.id}
                            to={`/templates?${t.owner ? `scope=personal&owner=${t.owner}` : ""}#t-${t.id}`}
                            className="block hover:text-accent-text hover:underline"
                            title={`Категория «${t.category}»`}
                          >
                            «{t.title}»
                            <span className="ml-1.5 text-[11.5px] text-fg-faint">{t.owner ? `личный · ${t.owner_name || t.owner}` : "общий"}</span>
                          </Link>
                        ))
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {data && data.built && data.phrases.length === 0 && <div className={`${card} p-4 text-[13px] text-fg-muted`}>Под отбор ничего не нашлось.</div>}
    </>
  );
}

export function TemplatesPage() {
  const { data: me } = useGetIdentity<CurrentUser>();
  const [get, set] = useParams();
  const tab = get("tab");
  if (!has(me, CLIENT_CHATS)) return <NoAccess what="шаблонам ответов" />;
  // У вкладок свой отбор: при переходе параметры другой вкладки из адреса уходят.
  const go = (tab: string) =>
    set({ tab, q: "", scope: "", owner: "", author: "", channel: "", days: "", short: "", auto: "", type: "", archive: "", t: "", pane: "" });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-annot="шапка шаблонов">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Шаблоны</h1>
        <div role="tablist" aria-label="Раздел шаблонов" className="flex gap-2">
          {[
            { id: "", label: "Шаблоны", hint: "все тексты на одном экране" },
            { id: "stats", label: "Частые ответы", hint: "что мы пишем на самом деле" },
            { id: "ai", label: "Шаблоны AI", hint: "как отвечает робот" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => go(t.id)}
              className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
                tab === t.id ? "border-accent bg-accent-dim text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              <span className="text-[15px] font-semibold">{t.label}</span>
              <span className="text-[12px] opacity-80">{t.hint}</span>
            </button>
          ))}
        </div>
      </div>
      {tab === "stats" ? (
        <StatsView get={get} set={set} />
      ) : tab === "ai" ? (
        <AiTemplatesView get={get} set={set} />
      ) : (
        <LibraryView get={get} set={set} />
      )}
    </main>
  );
}
